import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useRealTimeUpdates } from '../../../../Hooks/useRealTime';
import { 
  Card, Tabs, Table, Button, Space, Modal, message, Upload, Popconfirm, 
  Form, Input, InputNumber, Select, DatePicker, Tag, Row, Col, Divider, Progress, Empty, Spin, Checkbox, Tooltip 
} from 'antd';
import { 
  UploadOutlined, DownloadOutlined, PlusOutlined, DeleteOutlined, EditOutlined, CopyOutlined,
  CalendarOutlined, UserOutlined, UserAddOutlined, BookOutlined, BranchesOutlined, 
  ReadOutlined, ClearOutlined, BarChartOutlined 
} from '@ant-design/icons';
import Papa from 'papaparse';
import dayjs from 'dayjs';
import {
  fetchSubjects, updateSubject, deleteSubject, bulkUploadSubjects,
  fetchLessons, updateLesson, deleteLesson, bulkUploadLessons,
  fetchAcademicYears, createAcademicYear, updateAcademicYear, deleteAcademicYear,
  fetchStandards, createStandard, updateStandard, deleteStandard, copyStandardsToAcademicYear,
  fetchDailySessions
} from '../../../../Service/Syllabus_API/syllabusApi';
import {
  fetchInstructors, updateInstructor
} from '../../../../Service/Instructor_API/instructorApi';
import { fetchClusters, fetchAllClusterStandards } from '../../../../Service/Clusters_API/clustersApi';

const { TabPane } = Tabs;
const { Option } = Select;

const SyllabusManagementPage = () => {
  const [activeTab, setActiveTab] = useState('1');
  const [standards, setStandards] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [clusterStandardsList, setClusterStandardsList] = useState([]);
  const [dailySessions, setDailySessions] = useState([]);

  const [loading, setLoading] = useState(false);
  const [stdLoading, setStdLoading] = useState(false);
  const [ayLoading, setAyLoading] = useState(false);
  const [dsLoading, setDsLoading] = useState(false);

  // --- Active Academic Years (Used exclusively for Forms) ---
  const activeAcademicYears = academicYears.filter(
    ay => ay.status === 'Active' || ay.status === 'ACTIVE' || !ay.status
  );

  // --- Cascading State for Forms ---
  const [subjectFormAyId, setSubjectFormAyId] = useState(null);
  const [lessonFormAyId, setLessonFormAyId] = useState(null);
  const [lessonFormStdId, setLessonFormStdId] = useState(null);

  // --- Cascading State for View/Table Filters ---
  const [filterAyForStandards, setFilterAyForStandards] = useState(null);

  const [filterAyForSubjects, setFilterAyForSubjects] = useState(null);
  const [filterStdForSubjects, setFilterStdForSubjects] = useState(null);

  const [filterAyForLessons, setFilterAyForLessons] = useState(null);
  const [filterStdForLessons, setFilterStdForLessons] = useState(null);
  const [filterSubForLessons, setFilterSubForLessons] = useState(null);

  // --- Filter State for Syllabus Progress Tracker Tab ---
  const userRole = localStorage.getItem('role') || 'USER';
  const isStudio = userRole === 'STUDIO';
  const [filterAyForProgress, setFilterAyForProgress] = useState(null);
  const [filterClusterForProgress, setFilterClusterForProgress] = useState(null);
  const [filterStdForProgress, setFilterStdForProgress] = useState(null);
  const [filterSubForProgress, setFilterSubForProgress] = useState(null);

  // Standard Modal State
  const [isStandardModalOpen, setIsStandardModalOpen] = useState(false);
  const [editingStandard, setEditingStandard] = useState(null);
  const [standardForm] = Form.useForm();

  // Copy Standard & Syllabus Modal State
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [copyModalStandard, setCopyModalStandard] = useState(null);
  const [copyLoading, setCopyLoading] = useState(false);
  const [copyForm] = Form.useForm();
  
  // Subject Modal State (Multi-subject support)
  const [isSubjectModalOpen, setIsSubjectModalOpen] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [subjectForm] = Form.useForm();

  // Lesson Modal State (Multi-lesson support)
  const [isLessonModalOpen, setIsLessonModalOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [lessonForm] = Form.useForm();

  // Academic Year Modal State
  const [isAyModalOpen, setIsAyModalOpen] = useState(false);
  const [editingAy, setEditingAy] = useState(null);
  const [ayForm] = Form.useForm();

  // Assign Instructors to Subject Modal State
  const [isSubjectInstModalOpen, setIsSubjectInstModalOpen] = useState(false);
  const [selectedSubjectForInst, setSelectedSubjectForInst] = useState(null);
  const [subjectInstLoading, setSubjectInstLoading] = useState(false);
  const [subjectInstForm] = Form.useForm();

  // --- CSV Import Preview Modal State ---
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
  const [csvUploadType, setCsvUploadType] = useState('subjects'); // 'standards' | 'subjects' | 'lessons'
  const [csvParsedData, setCsvParsedData] = useState([]);
  const [uploadingCsv, setUploadingCsv] = useState(false);



  const handleRealTimeUpdate = () => {
    loadData(true);
  };

  useRealTimeUpdates('syllabus', handleRealTimeUpdate);
  useRealTimeUpdates('cluster_standard', handleRealTimeUpdate);

  const setDefaultAcademicYears = (ayList) => {
    if (!ayList || !Array.isArray(ayList) || ayList.length === 0) return;
    const activeAy = ayList.find(y => y.status === 'Active' || y.status === 'ACTIVE' || !y.status) || ayList[0];
    if (activeAy && activeAy.academic_year_id) {
      const activeId = activeAy.academic_year_id;
      setFilterAyForStandards(prev => (prev === null ? activeId : prev));
      setFilterAyForSubjects(prev => (prev === null ? activeId : prev));
      setFilterAyForLessons(prev => (prev === null ? activeId : prev));
      setFilterAyForProgress(prev => (prev === null ? activeId : prev));
    }
  };

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      if (activeTab === '1') {
        if (!silent) setStdLoading(true);
        const [stdRes, ayRes] = await Promise.all([
          fetchStandards(filterAyForStandards).catch(() => []),
          fetchAcademicYears().catch(() => [])
        ]);
        if (stdRes && stdRes.length !== undefined) setStandards(stdRes);
        if (ayRes && ayRes.length !== undefined) {
          setAcademicYears(ayRes);
          setDefaultAcademicYears(ayRes);
        }
        if (!silent) setStdLoading(false);
      } else if (activeTab === '2') {
        const [subRes, instRes, ayRes, stdRes] = await Promise.all([
          fetchSubjects(filterStdForSubjects, filterAyForSubjects).catch(() => []),
          fetchInstructors().catch(() => []),
          fetchAcademicYears().catch(() => []),
          fetchStandards(filterAyForSubjects).catch(() => [])
        ]);
        if (subRes && subRes.length !== undefined) setSubjects(subRes);
        if (instRes && instRes.length !== undefined) setInstructors(instRes);
        if (ayRes && ayRes.length !== undefined) {
          setAcademicYears(ayRes);
          setDefaultAcademicYears(ayRes);
        }
        if (stdRes && stdRes.length !== undefined) setStandards(stdRes);
      } else if (activeTab === '3') {
        const [lesRes, subRes, stdRes, ayRes] = await Promise.all([
          fetchLessons(filterStdForLessons, filterSubForLessons, filterAyForLessons).catch(() => []),
          fetchSubjects(filterStdForLessons, filterAyForLessons).catch(() => []),
          fetchStandards(filterAyForLessons).catch(() => []),
          fetchAcademicYears().catch(() => [])
        ]);
        if (lesRes && lesRes.length !== undefined) setLessons(lesRes);
        if (subRes && subRes.length !== undefined) setSubjects(subRes);
        if (stdRes && stdRes.length !== undefined) setStandards(stdRes);
        if (ayRes && ayRes.length !== undefined) {
          setAcademicYears(ayRes);
          setDefaultAcademicYears(ayRes);
        }
      } else if (activeTab === '4') {
        if (!silent) setAyLoading(true);
        const res = await fetchAcademicYears();
        if (res && res.length !== undefined) {
          setAcademicYears(res);
          setDefaultAcademicYears(res);
        }
        if (!silent) setAyLoading(false);
      } else if (activeTab === '5') {
        if (!silent) setDsLoading(true);
        const [subRes, stdRes, ayRes, lesRes, dsRes, clusRes, csRes] = await Promise.all([
          fetchSubjects().catch(() => []),
          fetchStandards().catch(() => []),
          fetchAcademicYears().catch(() => []),
          fetchLessons().catch(() => []),
          fetchDailySessions().catch(() => []),
          fetchClusters().catch(() => []),
          fetchAllClusterStandards(filterAyForProgress).catch(() => [])
        ]);
        if (subRes && subRes.length !== undefined) setSubjects(subRes);
        if (stdRes && stdRes.length !== undefined) setStandards(stdRes);
        if (ayRes && ayRes.length !== undefined) {
          setAcademicYears(ayRes);
          setDefaultAcademicYears(ayRes);
        }
        if (lesRes && lesRes.length !== undefined) setLessons(lesRes);
        if (dsRes && dsRes.length !== undefined) setDailySessions(dsRes);
        if (clusRes && clusRes.length !== undefined) setClusters(clusRes);
        if (csRes && csRes.length !== undefined) setClusterStandardsList(csRes);
        if (!silent) setDsLoading(false);
      }
    } catch (err) {
      if (!silent) message.error('Failed to load data');
    } finally {
      if (!silent) {
        setLoading(false);
        setStdLoading(false);
        setAyLoading(false);
        setDsLoading(false);
      }
    }
  }, [activeTab, filterAyForStandards, filterAyForSubjects, filterStdForSubjects, filterAyForLessons, filterStdForLessons, filterSubForLessons, filterAyForProgress]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // --- Dynamic Table Data Filtering Logic ---
  // Standards Table Data
  const filteredStandards = standards.filter(s => {
    if (filterAyForStandards && s.academic_year_id && String(s.academic_year_id) !== String(filterAyForStandards)) return false;
    return true;
  });

  // Standards available for Subject Filter dropdown
  const standardsForSubjectFilter = filterAyForSubjects
    ? standards.filter(s => !s.academic_year_id || String(s.academic_year_id) === String(filterAyForSubjects))
    : standards;

  // Subjects Table Data
  const filteredSubjects = subjects.filter(sub => {
    if (filterAyForSubjects && sub.academic_year_id && String(sub.academic_year_id) !== String(filterAyForSubjects)) return false;
    if (filterStdForSubjects && String(sub.standard_id) !== String(filterStdForSubjects)) return false;
    return true;
  });

  // Standards available for Lesson Filter dropdown
  const standardsForLessonFilter = filterAyForLessons
    ? standards.filter(s => !s.academic_year_id || String(s.academic_year_id) === String(filterAyForLessons))
    : standards;

  // Subjects available for Lesson Filter dropdown
  const subjectsForLessonFilter = subjects.filter(sub => {
    const matchAy = !filterAyForLessons || !sub.academic_year_id || String(sub.academic_year_id) === String(filterAyForLessons);
    const matchStd = !filterStdForLessons || String(sub.standard_id) === String(filterStdForLessons);
    return matchAy && matchStd;
  });

  const sortLessonsAscending = (lessonList) => {
    if (!Array.isArray(lessonList)) return [];
    return [...lessonList].sort((a, b) => {
      const nameA = a.lesson_name || `Lesson #${a.lessons_id || 0}`;
      const nameB = b.lesson_name || `Lesson #${b.lessons_id || 0}`;
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
    });
  };

  // Lessons Table Data
  const filteredLessons = sortLessonsAscending(lessons.filter(l => {
    if (filterStdForLessons && String(l.standard_id) !== String(filterStdForLessons)) return false;
    if (filterSubForLessons && String(l.subjects_id) !== String(filterSubForLessons)) return false;
    if (filterAyForLessons) {
      const lesAy = l.academic_year_id;
      const subObj = subjects.find(s => String(s.subjects_id) === String(l.subjects_id));
      const stdObj = standards.find(s => String(s.standard_id) === String(l.standard_id));
      const subAy = subObj ? subObj.academic_year_id : null;
      const stdAy = stdObj ? stdObj.academic_year_id : null;
      const effectiveAy = lesAy || subAy || stdAy;
      if (effectiveAy && String(effectiveAy) !== String(filterAyForLessons)) return false;
    }
    return true;
  }));

  // --- Syllabus Progress Tracker Data Filtering (Cluster-Wise Cascading Filters) ---
  const availableClustersForProgress = useMemo(() => {
    let clusList = clusters || [];
    if (filterAyForProgress) {
      clusList = clusList.filter(c => !c.academic_year_id || String(c.academic_year_id) === String(filterAyForProgress));
    }
    return [...clusList].sort((a, b) => (a.cluster_name || '').localeCompare(b.cluster_name || '', undefined, { numeric: true }));
  }, [clusters, filterAyForProgress]);

  const standardsForProgressFilter = useMemo(() => {
    let stdList = standards || [];
    if (filterAyForProgress) {
      stdList = stdList.filter(s => !s.academic_year_id || String(s.academic_year_id) === String(filterAyForProgress));
    }

    if (filterClusterForProgress) {
      const linkedClusterStandards = clusterStandardsList.filter(cs => String(cs.cluster_id) === String(filterClusterForProgress));
      let linkedStdIds = new Set(linkedClusterStandards.map(cs => String(cs.standard_id)));

      // Fallback: If no cluster_standard mapping rows are populated in DB for this cluster,
      // check standards that have cluster_id === filterClusterForProgress or daily sessions recorded for this cluster
      if (linkedStdIds.size === 0) {
        const dsStdIds = dailySessions
          .filter(ds => String(ds.cluster_id) === String(filterClusterForProgress))
          .map(ds => String(ds.standard_id))
          .filter(Boolean);
        const stClusterIds = (standards || [])
          .filter(s => s.cluster_id && String(s.cluster_id) === String(filterClusterForProgress))
          .map(s => String(s.standard_id));
        linkedStdIds = new Set([...dsStdIds, ...stClusterIds]);
      }

      if (linkedStdIds.size > 0) {
        stdList = stdList.filter(s => linkedStdIds.has(String(s.standard_id)));
      }
    }

    return [...stdList].sort((a, b) => (a.standard_name || '').localeCompare(b.standard_name || '', undefined, { numeric: true }));
  }, [standards, filterAyForProgress, filterClusterForProgress, clusterStandardsList, dailySessions]);

  const subjectsForProgressFilter = useMemo(() => {
    let clusterLinkedStdIds = null;
    if (filterClusterForProgress && !filterStdForProgress) {
      const linked = clusterStandardsList
        .filter(cs => String(cs.cluster_id) === String(filterClusterForProgress))
        .map(cs => String(cs.standard_id));
      if (linked.length > 0) {
        clusterLinkedStdIds = new Set(linked);
      } else {
        const dsStdIds = dailySessions
          .filter(ds => String(ds.cluster_id) === String(filterClusterForProgress))
          .map(ds => String(ds.standard_id))
          .filter(Boolean);
        const stClusterIds = (standards || [])
          .filter(s => s.cluster_id && String(s.cluster_id) === String(filterClusterForProgress))
          .map(s => String(s.standard_id));
        const fallbackSet = new Set([...dsStdIds, ...stClusterIds]);
        if (fallbackSet.size > 0) clusterLinkedStdIds = fallbackSet;
      }
    }

    return (subjects || []).filter(sub => {
      const matchAy = !filterAyForProgress || !sub.academic_year_id || String(sub.academic_year_id) === String(filterAyForProgress);
      const matchStd = filterStdForProgress
        ? String(sub.standard_id) === String(filterStdForProgress)
        : (!clusterLinkedStdIds || clusterLinkedStdIds.has(String(sub.standard_id)));
      return matchAy && matchStd;
    }).sort((a, b) => (a.subjects_name || '').localeCompare(b.subjects_name || '', undefined, { numeric: true }));
  }, [subjects, filterAyForProgress, filterStdForProgress, filterClusterForProgress, clusterStandardsList, dailySessions, standards]);

  const getLessonProgressForCluster = useCallback((lessonId, clusterId) => {
    const matchingDs = dailySessions.filter(ds => {
      const matchLesson = String(ds.lessons_id) === String(lessonId);
      const matchClus = !clusterId || String(ds.cluster_id) === String(clusterId);
      return matchLesson && matchClus;
    });
    
    const usedSessions = matchingDs.reduce((acc, ds) => {
      const count = ds.session_count !== undefined && ds.session_count !== null ? Number(ds.session_count) : 1;
      return acc + count;
    }, 0);

    let status = '';
    for (let i = matchingDs.length - 1; i >= 0; i--) {
      if (matchingDs[i].lesson_status && String(matchingDs[i].lesson_status).trim() !== '') {
        status = String(matchingDs[i].lesson_status).trim();
        break;
      }
    }

    return { usedSessions, status };
  }, [dailySessions]);

  const getSubjectProgressForCluster = useCallback((subjectId, clusterId) => {
    const subLessons = lessons.filter(l => String(l.subjects_id) === String(subjectId));
    const totalAllocated = subLessons.reduce((acc, l) => acc + (Number(l.lesson_total_sessions) || 1), 0);
    
    let totalUsed = 0;
    subLessons.forEach(l => {
      const { usedSessions } = getLessonProgressForCluster(l.lessons_id, clusterId);
      totalUsed += usedSessions;
    });

    const remaining = Math.max(0, totalAllocated - totalUsed);
    const percent = totalAllocated > 0 ? Math.min(100, Math.round((totalUsed / totalAllocated) * 100)) : 0;
    return { totalAllocated, totalUsed, remaining, percent, lessonCount: subLessons.length, subLessons };
  }, [lessons, getLessonProgressForCluster]);

  // Grouped Progress Tracker Data by Cluster (Only checking standards linked to each cluster)
  const clusterProgressGroups = React.useMemo(() => {
    let targetClusters = availableClustersForProgress;
    if (filterClusterForProgress) {
      targetClusters = targetClusters.filter(c => String(c.cluster_id) === String(filterClusterForProgress));
    }

    if (!targetClusters || targetClusters.length === 0) {
      if (filterClusterForProgress) {
        const c = clusters.find(cl => String(cl.cluster_id) === String(filterClusterForProgress));
        if (c) targetClusters = [c];
      }
    }

    return targetClusters.map(c => {
      const cId = c.cluster_id;
      const cName = c.cluster_name || `Cluster #${cId}`;
      const cCode = c.cluster_code || '';
      const cAyId = c.academic_year_id;

      // 1. Get ONLY the standards explicitly linked to this cluster in cluster_standard mapping
      const linkedClusterStandards = clusterStandardsList.filter(cs => String(cs.cluster_id) === String(cId));
      let linkedStdIds = new Set(linkedClusterStandards.map(cs => String(cs.standard_id)));

      // Fallback: If no cluster_standard mapping rows are populated in DB for this cluster,
      // check standards that have cluster_id === cId or daily sessions recorded for this cluster
      if (linkedStdIds.size === 0) {
        const dsStdIds = dailySessions
          .filter(ds => String(ds.cluster_id) === String(cId))
          .map(ds => String(ds.standard_id))
          .filter(Boolean);
        const stClusterIds = standards
          .filter(s => s.cluster_id && String(s.cluster_id) === String(cId))
          .map(s => String(s.standard_id));
        linkedStdIds = new Set([...dsStdIds, ...stClusterIds]);
      }

      // Filter subjects: ONLY include subjects whose standard is linked to this cluster
      const clusterSubjects = subjects.filter(sub => {
        if (!linkedStdIds.has(String(sub.standard_id))) return false;
        if (filterAyForProgress && sub.academic_year_id && String(sub.academic_year_id) !== String(filterAyForProgress)) return false;
        if (!filterAyForProgress && cAyId && sub.academic_year_id && String(sub.academic_year_id) !== String(cAyId)) return false;
        if (filterStdForProgress && String(sub.standard_id) !== String(filterStdForProgress)) return false;
        if (filterSubForProgress && String(sub.subjects_id) !== String(filterSubForProgress)) return false;
        return true;
      });

      let totalAllocatedSessions = 0;
      let totalUsedSessions = 0;
      let totalLessons = 0;

      const subjectsWithProgress = clusterSubjects.map(sub => {
        const stdObj = standards.find(s => String(s.standard_id) === String(sub.standard_id));
        const ayObj = academicYears.find(ay => String(ay.academic_year_id) === String(sub.academic_year_id || cAyId));
        const progress = getSubjectProgressForCluster(sub.subjects_id, cId);

        totalAllocatedSessions += progress.totalAllocated;
        totalUsedSessions += progress.totalUsed;
        totalLessons += progress.lessonCount;

        return {
          ...sub,
          standard_name: stdObj?.standard_name || sub.subjects_standard || '—',
          standard_medium: stdObj?.standard_medium || 'English',
          academic_year_name: ayObj?.academic_year || '—',
          progress
        };
      });

      const overallPercent = totalAllocatedSessions > 0
        ? Math.min(100, Math.round((totalUsedSessions / totalAllocatedSessions) * 100))
        : 0;

      const totalRemainingSessions = Math.max(0, totalAllocatedSessions - totalUsedSessions);

      return {
        clusterId: cId,
        clusterName: cName,
        clusterCode: cCode,
        academicYearName: academicYears.find(ay => String(ay.academic_year_id) === String(cAyId))?.academic_year || 'All Years',
        linkedStandardsCount: linkedStdIds.size,
        subjects: subjectsWithProgress,
        totalSubjects: subjectsWithProgress.length,
        totalLessons,
        totalAllocatedSessions,
        totalUsedSessions,
        totalRemainingSessions,
        overallPercent
      };
    })
    .filter(cg => cg.linkedStandardsCount > 0 || cg.subjects.length > 0)
    .sort((a, b) => (a.clusterName || '').localeCompare(b.clusterName || ''));
  }, [availableClustersForProgress, filterClusterForProgress, filterAyForProgress, filterStdForProgress, filterSubForProgress, subjects, standards, academicYears, clusters, clusterStandardsList, dailySessions, getSubjectProgressForCluster]);

  useEffect(() => {
    if (isStudio && availableClustersForProgress.length > 0) {
      if (!filterClusterForProgress || !availableClustersForProgress.some(c => c.cluster_id === filterClusterForProgress)) {
        setFilterClusterForProgress(availableClustersForProgress[0].cluster_id);
      }
    }
  }, [isStudio, availableClustersForProgress, filterClusterForProgress]);

  // --- Form Cascading Options ---
  const availableStandardsForSubjectModal = subjectFormAyId
    ? standards.filter(s => !s.academic_year_id || String(s.academic_year_id) === String(subjectFormAyId))
    : standards;

  const availableStandardsForLessonModal = lessonFormAyId
    ? standards.filter(s => !s.academic_year_id || String(s.academic_year_id) === String(lessonFormAyId))
    : standards;

  const availableSubjectsForLessonModal = subjects.filter(sub => {
    const matchAy = !lessonFormAyId || !sub.academic_year_id || String(sub.academic_year_id) === String(lessonFormAyId);
    const matchStd = !lessonFormStdId || String(sub.standard_id) === String(lessonFormStdId);
    return matchAy && matchStd;
  });

  const handleCsvUpload = (file, type) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const rawData = results.data;
        if (!rawData || rawData.length === 0) {
          message.warning('CSV file is empty or invalid.');
          return;
        }

        const parsedRows = [];

        if (type === 'subjects') {
          rawData.forEach((row) => {
            const values = Object.values(row).filter(v => v !== undefined && String(v).trim() !== '');
            if (values.length === 0) return;

            const getVal = (...keys) => {
              for (const k of keys) {
                const foundKey = Object.keys(row).find(rk => rk.trim().toLowerCase().replace(/[\s_-]/g, '') === k.toLowerCase().replace(/[\s_-]/g, ''));
                if (foundKey && row[foundKey] !== undefined && String(row[foundKey]).trim() !== '') {
                  return String(row[foundKey]).trim();
                }
              }
              return '';
            };

            const subName = getVal('subjects_name', 'subject_name', 'name', 'subject', 'title');
            if (!subName) return;

            const subCode = getVal('subjects_code', 'subject_code', 'code');
            const subMedium = getVal('subjects_medium', 'subject_medium', 'medium') || 'ENGLISH';
            const stdRaw = getVal('standard_id', 'standard_name', 'standard', 'subjects_standard', 'grade');
            const ayRaw = getVal('academic_year_id', 'academic_year', 'ay');

            // Resolve Standard ID and Name
            let matchedStd = standards.find(s => String(s.standard_id) === String(stdRaw) || (s.standard_name && s.standard_name.trim().toLowerCase() === stdRaw.toLowerCase()));
            if (!matchedStd && filterStdForSubjects) {
              matchedStd = standards.find(s => String(s.standard_id) === String(filterStdForSubjects));
            }
            const standardId = matchedStd ? matchedStd.standard_id : (stdRaw && !isNaN(stdRaw) ? parseInt(stdRaw) : (filterStdForSubjects || null));
            const standardName = matchedStd ? matchedStd.standard_name : (standards.find(s => String(s.standard_id) === String(standardId))?.standard_name || stdRaw || '—');

            // Resolve Academic Year ID and Name
            let matchedAy = academicYears.find(a => String(a.academic_year_id) === String(ayRaw) || (a.academic_year && a.academic_year.trim().toLowerCase() === ayRaw.toLowerCase()));
            if (!matchedAy && filterAyForSubjects) {
              matchedAy = academicYears.find(a => String(a.academic_year_id) === String(filterAyForSubjects));
            }
            if (!matchedAy && activeAcademicYears.length > 0) {
              matchedAy = activeAcademicYears[0];
            }
            const ayId = matchedAy ? matchedAy.academic_year_id : (ayRaw && !isNaN(ayRaw) ? parseInt(ayRaw) : (filterAyForSubjects || null));
            const ayName = matchedAy ? matchedAy.academic_year : (academicYears.find(a => String(a.academic_year_id) === String(ayId))?.academic_year || ayRaw || '—');

            parsedRows.push({
              subjects_name: subName,
              subjects_code: subCode || '',
              subjects_medium: subMedium,
              subjects_standard: standardName,
              standard_id: standardId,
              standard_name: standardName,
              academic_year_id: ayId,
              academic_year_name: ayName
            });
          });
        } else if (type === 'lessons') {
          rawData.forEach((row) => {
            const values = Object.values(row).filter(v => v !== undefined && String(v).trim() !== '');
            if (values.length === 0) return;

            const getVal = (...keys) => {
              for (const k of keys) {
                const foundKey = Object.keys(row).find(rk => rk.trim().toLowerCase().replace(/[\s_-]/g, '') === k.toLowerCase().replace(/[\s_-]/g, ''));
                if (foundKey && row[foundKey] !== undefined && String(row[foundKey]).trim() !== '') {
                  return String(row[foundKey]).trim();
                }
              }
              return '';
            };

            const lesName = getVal('lesson_name', 'name', 'lesson', 'topic');
            if (!lesName) return;

            const lesMedium = getVal('lesson_medium', 'medium') || 'ENGLISH';
            const totalSessions = parseInt(getVal('lesson_total_sessions', 'total_sessions', 'sessions', 'count')) || 1;
            const subRaw = getVal('subjects_id', 'subjects_name', 'subject_name', 'subject');
            const stdRaw = getVal('standard_id', 'standard_name', 'standard', 'grade');
            const ayRaw = getVal('academic_year_id', 'academic_year', 'ay');

            // Resolve Subject
            let matchedSub = subjects.find(s => String(s.subjects_id) === String(subRaw) || (s.subjects_name && s.subjects_name.trim().toLowerCase() === subRaw.toLowerCase()));
            if (!matchedSub && filterSubForLessons) {
              matchedSub = subjects.find(s => String(s.subjects_id) === String(filterSubForLessons));
            }
            const subId = matchedSub ? matchedSub.subjects_id : (subRaw && !isNaN(subRaw) ? parseInt(subRaw) : (filterSubForLessons || null));
            const subName = matchedSub ? matchedSub.subjects_name : (subjects.find(s => String(s.subjects_id) === String(subId))?.subjects_name || subRaw || '—');

            // Resolve Standard
            let matchedStd = standards.find(s => String(s.standard_id) === String(stdRaw) || (s.standard_name && s.standard_name.trim().toLowerCase() === stdRaw.toLowerCase()));
            if (!matchedStd && matchedSub && matchedSub.standard_id) {
              matchedStd = standards.find(s => String(s.standard_id) === String(matchedSub.standard_id));
            }
            if (!matchedStd && filterStdForLessons) {
              matchedStd = standards.find(s => String(s.standard_id) === String(filterStdForLessons));
            }
            const standardId = matchedStd ? matchedStd.standard_id : (stdRaw && !isNaN(stdRaw) ? parseInt(stdRaw) : (filterStdForLessons || null));
            const standardName = matchedStd ? matchedStd.standard_name : (standards.find(s => String(s.standard_id) === String(standardId))?.standard_name || stdRaw || '—');

            // Resolve Academic Year
            let matchedAy = academicYears.find(a => String(a.academic_year_id) === String(ayRaw) || (a.academic_year && a.academic_year.trim().toLowerCase() === ayRaw.toLowerCase()));
            if (!matchedAy && matchedSub && matchedSub.academic_year_id) {
              matchedAy = academicYears.find(a => String(a.academic_year_id) === String(matchedSub.academic_year_id));
            }
            if (!matchedAy && filterAyForLessons) {
              matchedAy = academicYears.find(a => String(a.academic_year_id) === String(filterAyForLessons));
            }
            if (!matchedAy && activeAcademicYears.length > 0) {
              matchedAy = activeAcademicYears[0];
            }
            const ayId = matchedAy ? matchedAy.academic_year_id : (ayRaw && !isNaN(ayRaw) ? parseInt(ayRaw) : (filterAyForLessons || null));
            const ayName = matchedAy ? matchedAy.academic_year : (academicYears.find(a => String(a.academic_year_id) === String(ayId))?.academic_year || ayRaw || '—');

            parsedRows.push({
              lesson_name: lesName,
              lesson_medium: lesMedium,
              lesson_total_sessions: totalSessions,
              subjects_id: subId,
              subjects_name: subName,
              standard_id: standardId,
              standard_name: standardName,
              academic_year_id: ayId,
              academic_year_name: ayName
            });
          });
        } else if (type === 'standards') {
          rawData.forEach((row) => {
            const values = Object.values(row).filter(v => v !== undefined && String(v).trim() !== '');
            if (values.length === 0) return;

            const getVal = (...keys) => {
              for (const k of keys) {
                const foundKey = Object.keys(row).find(rk => rk.trim().toLowerCase().replace(/[\s_-]/g, '') === k.toLowerCase().replace(/[\s_-]/g, ''));
                if (foundKey && row[foundKey] !== undefined && String(row[foundKey]).trim() !== '') {
                  return String(row[foundKey]).trim();
                }
              }
              return '';
            };

            const stdName = getVal('standard_name', 'name', 'standard', 'grade');
            if (!stdName) return;

            const stdMedium = getVal('standard_medium', 'medium') || 'ENGLISH';
            const stdStream = getVal('standard_stream', 'stream') || '';
            const ayRaw = getVal('academic_year_id', 'academic_year', 'ay');

            let matchedAy = academicYears.find(a => String(a.academic_year_id) === String(ayRaw) || (a.academic_year && a.academic_year.trim().toLowerCase() === ayRaw.toLowerCase()));
            if (!matchedAy && filterAyForStandards) {
              matchedAy = academicYears.find(a => String(a.academic_year_id) === String(filterAyForStandards));
            }
            if (!matchedAy && activeAcademicYears.length > 0) {
              matchedAy = activeAcademicYears[0];
            }
            const ayId = matchedAy ? matchedAy.academic_year_id : (ayRaw && !isNaN(ayRaw) ? parseInt(ayRaw) : (filterAyForStandards || null));
            const ayName = matchedAy ? matchedAy.academic_year : (academicYears.find(a => String(a.academic_year_id) === String(ayId))?.academic_year || ayRaw || '—');

            parsedRows.push({
              standard_name: stdName,
              standard_medium: stdMedium,
              standard_stream: stdStream,
              academic_year_id: ayId,
              academic_year_name: ayName
            });
          });
        }

        if (parsedRows.length === 0) {
          message.error(`No valid ${type} records found in CSV file.`);
          return;
        }

        setCsvParsedData(parsedRows);
        setCsvUploadType(type);
        setIsCsvModalOpen(true);
      },
      error: () => {
        message.error('Failed to parse CSV file.');
      }
    });
    return false;
  };

  const handleConfirmCsvUpload = async () => {
    setUploadingCsv(true);
    try {
      if (csvUploadType === 'subjects') {
        await bulkUploadSubjects({ subjects: csvParsedData });
        message.success(`Successfully imported ${csvParsedData.length} subjects!`);
      } else if (csvUploadType === 'lessons') {
        await bulkUploadLessons({ lessons: csvParsedData });
        message.success(`Successfully imported ${csvParsedData.length} lessons!`);
      } else if (csvUploadType === 'standards') {
        for (const std of csvParsedData) {
          await createStandard(std);
        }
        message.success(`Successfully imported ${csvParsedData.length} standards!`);
      }
      setIsCsvModalOpen(false);
      setCsvParsedData([]);
      loadData();
    } catch (error) {
      message.error(error.message || `Failed to upload ${csvUploadType}`);
    } finally {
      setUploadingCsv(false);
    }
  };

  const downloadCSV = (type) => {
    const data = type === 'standards' ? filteredStandards : type === 'subjects' ? filteredSubjects : filteredLessons;
    if (!data || data.length === 0) {
      message.warning('No data to download.');
      return;
    }
    
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${type}_export.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async (id, type) => {
    try {
      if (type === 'standards') {
        await deleteStandard(id);
        message.success('Standard deleted');
      } else if (type === 'subjects') {
        await deleteSubject(id);
        message.success('Subject deleted');
      } else if (type === 'lessons') {
        await deleteLesson(id);
        message.success('Lesson deleted');
      } else if (type === 'academic_year') {
        await deleteAcademicYear(id);
        message.success('Academic year deleted');
      }
      loadData();
    } catch (err) {
      message.error('Failed to delete');
    }
  };

  // --- Standard Modal Handlers ---
  const handleStandardModalOpen = async (record = null) => {
    setEditingStandard(record);
    if (academicYears.length === 0) {
      const ayRes = await fetchAcademicYears().catch(() => []);
      if (ayRes && ayRes.length) setAcademicYears(ayRes);
    }

    const defaultAy = activeAcademicYears[0]?.academic_year_id || academicYears[0]?.academic_year_id || null;

    if (record) {
      standardForm.setFieldsValue({
        academic_year_id: record.academic_year_id || defaultAy,
        standard_name: record.standard_name,
        standard_code: record.standard_code,
        standard_medium: record.standard_medium
      });
    } else {
      standardForm.setFieldsValue({
        academic_year_id: defaultAy,
        standard_name: '',
        standard_code: '',
        standard_medium: 'English'
      });
    }
    setIsStandardModalOpen(true);
  };

  const handleStandardSubmit = async (values) => {
    try {
      const payload = {
        academic_year_id: values.academic_year_id,
        standard_name: values.standard_name,
        standard_code: values.standard_code,
        standard_medium: values.standard_medium
      };

      if (editingStandard) {
        await updateStandard(editingStandard.standard_id, payload);
        message.success('Standard updated successfully');
      } else {
        await createStandard(payload);
        message.success('Standard created successfully');
      }
      setIsStandardModalOpen(false);
      loadData();
    } catch (error) {
      message.error('Failed to save standard');
    }
  };

  // --- Copy Standard & Syllabus Modal Handlers ---
  const handleOpenCopyModal = async (record = null) => {
    setCopyModalStandard(record);
    if (academicYears.length === 0 || standards.length === 0) {
      const [ayRes, stdRes] = await Promise.all([
        fetchAcademicYears().catch(() => []),
        fetchStandards().catch(() => [])
      ]);
      if (ayRes && ayRes.length) setAcademicYears(ayRes);
      if (stdRes && stdRes.length) setStandards(stdRes);
    }

    const srcAyId = record ? record.academic_year_id : (filterAyForStandards || activeAcademicYears[0]?.academic_year_id);
    const otherAys = academicYears.filter(ay => String(ay.academic_year_id) !== String(srcAyId));
    const defaultTargetAy = otherAys.length > 0 ? otherAys[0].academic_year_id : null;

    let selectedStdIds = [];
    if (record) {
      selectedStdIds = [record.standard_id];
    } else if (filterAyForStandards) {
      selectedStdIds = standards
        .filter(s => String(s.academic_year_id) === String(filterAyForStandards))
        .map(s => s.standard_id);
    }

    copyForm.setFieldsValue({
      source_standard_ids: selectedStdIds,
      target_academic_year_id: defaultTargetAy,
      copy_subjects: true,
      copy_lessons: true
    });
    setIsCopyModalOpen(true);
  };

  const handleCopySubmit = async (values) => {
    setCopyLoading(true);
    try {
      const payload = {
        source_standard_ids: values.source_standard_ids,
        target_academic_year_id: values.target_academic_year_id,
        copy_subjects: values.copy_subjects !== false,
        copy_lessons: values.copy_lessons !== false
      };

      const res = await copyStandardsToAcademicYear(payload);
      message.success(res.message || 'Standard and Syllabus copied successfully!');
      setIsCopyModalOpen(false);
      copyForm.resetFields();
      loadData();
    } catch (error) {
      message.error(error.message || 'Failed to copy standard and syllabus');
    } finally {
      setCopyLoading(false);
    }
  };

  // --- Subject Modal Handlers ---
  const handleSubjectModalOpen = async (record = null) => {
    setEditingSubject(record);
    if (standards.length === 0 || academicYears.length === 0) {
      const [stdRes, ayRes] = await Promise.all([
        fetchStandards().catch(() => []),
        fetchAcademicYears().catch(() => [])
      ]);
      if (stdRes && stdRes.length) setStandards(stdRes);
      if (ayRes && ayRes.length) setAcademicYears(ayRes);
    }

    const defaultAy = activeAcademicYears[0]?.academic_year_id || academicYears[0]?.academic_year_id || null;
    const initialAy = record ? record.academic_year_id : defaultAy;
    setSubjectFormAyId(initialAy);

    const validStds = initialAy 
      ? standards.filter(s => !s.academic_year_id || String(s.academic_year_id) === String(initialAy))
      : standards;

    const defaultStd = validStds[0]?.standard_id || null;

    if (record) {
      subjectForm.setFieldsValue({
        academic_year_id: record.academic_year_id,
        standard_id: record.standard_id,
        subjects_name: record.subjects_name,
        subjects_code: record.subjects_code,
        subjects_medium: record.subjects_medium || 'English'
      });
    } else {
      subjectForm.setFieldsValue({
        academic_year_id: defaultAy,
        standard_id: defaultStd,
        subjects: [
          { subjects_name: '', subjects_code: '', subjects_medium: 'English' }
        ]
      });
    }
    setIsSubjectModalOpen(true);
  };

  const handleSubjectSubmit = async (values) => {
    try {
      setLoading(true);
      if (editingSubject) {
        const payload = {
          standard_id: values.standard_id,
          academic_year_id: values.academic_year_id,
          subjects_name: values.subjects_name,
          subjects_code: values.subjects_code,
          subjects_medium: values.subjects_medium
        };
        await updateSubject(editingSubject.subjects_id, payload);
        message.success('Subject updated successfully');
      } else {
        const subList = values.subjects || [];
        const validSubjects = subList.filter(s => s && s.subjects_name && s.subjects_name.trim() !== '');
        if (validSubjects.length === 0) {
          message.warning('Please enter at least one subject name');
          setLoading(false);
          return;
        }
        const payloadSubjects = validSubjects.map(s => ({
          standard_id: values.standard_id,
          academic_year_id: values.academic_year_id,
          subjects_name: s.subjects_name.trim(),
          subjects_code: s.subjects_code ? s.subjects_code.trim() : null,
          subjects_medium: s.subjects_medium || 'English'
        }));
        await bulkUploadSubjects({ subjects: payloadSubjects });
        message.success(`${payloadSubjects.length} subject(s) added successfully`);
      }
      setIsSubjectModalOpen(false);
      setEditingSubject(null);
      subjectForm.resetFields();
      loadData();
    } catch (error) {
      message.error('Failed to save subject(s)');
    } finally {
      setLoading(false);
    }
  };

  // --- Lesson Modal Handlers ---
  const handleLessonModalOpen = async (record = null) => {
    setEditingLesson(record);
    if (standards.length === 0 || subjects.length === 0 || academicYears.length === 0) {
      const [stdRes, subRes, ayRes] = await Promise.all([
        fetchStandards().catch(() => []),
        fetchSubjects().catch(() => []),
        fetchAcademicYears().catch(() => [])
      ]);
      if (stdRes && stdRes.length) setStandards(stdRes);
      if (subRes && subRes.length) setSubjects(subRes);
      if (ayRes && ayRes.length) setAcademicYears(ayRes);
    }

    const defaultAy = activeAcademicYears[0]?.academic_year_id || academicYears[0]?.academic_year_id || null;
    const initialAy = record ? record.academic_year_id : defaultAy;
    const initialStd = record ? record.standard_id : null;

    setLessonFormAyId(initialAy);
    setLessonFormStdId(initialStd);

    if (record) {
      lessonForm.setFieldsValue({
        academic_year_id: record.academic_year_id || defaultAy,
        standard_id: record.standard_id,
        subjects_id: record.subjects_id,
        lesson_name: record.lesson_name,
        lesson_total_sessions: record.lesson_total_sessions || 1
      });
    } else {
      const validStds = initialAy 
        ? standards.filter(s => !s.academic_year_id || String(s.academic_year_id) === String(initialAy))
        : standards;
      const defaultStd = validStds[0]?.standard_id || null;
      setLessonFormStdId(defaultStd);

      const validSubs = subjects.filter(sub => {
        const matchAy = !initialAy || !sub.academic_year_id || String(sub.academic_year_id) === String(initialAy);
        const matchStd = !defaultStd || String(sub.standard_id) === String(defaultStd);
        return matchAy && matchStd;
      });
      const defaultSub = validSubs[0]?.subjects_id || null;

      lessonForm.setFieldsValue({
        academic_year_id: defaultAy,
        standard_id: defaultStd,
        subjects_id: defaultSub,
        lessons: [
          { lesson_name: '', lesson_total_sessions: 1 }
        ]
      });
    }
    setIsLessonModalOpen(true);
  };

  const handleLessonSubmit = async (values) => {
    try {
      setLoading(true);
      if (editingLesson) {
        const payload = {
          standard_id: values.standard_id,
          subjects_id: values.subjects_id,
          academic_year_id: values.academic_year_id,
          lesson_name: values.lesson_name,
          lesson_total_sessions: Number(values.lesson_total_sessions) || 1
        };
        await updateLesson(editingLesson.lessons_id, payload);
        message.success('Lesson updated successfully');
      } else {
        const lessonList = values.lessons || [];
        const validLessons = lessonList.filter(l => l && l.lesson_name && l.lesson_name.trim() !== '');
        if (validLessons.length === 0) {
          message.warning('Please enter at least one lesson name');
          setLoading(false);
          return;
        }
        const payloadLessons = validLessons.map(l => ({
          standard_id: values.standard_id,
          subjects_id: values.subjects_id,
          academic_year_id: values.academic_year_id,
          lesson_name: l.lesson_name.trim(),
          lesson_total_sessions: Number(l.lesson_total_sessions) || 1
        }));
        await bulkUploadLessons({ lessons: payloadLessons });
        message.success(`${payloadLessons.length} lesson(s) added successfully`);
      }
      setIsLessonModalOpen(false);
      setEditingLesson(null);
      lessonForm.resetFields();
      loadData();
    } catch (error) {
      message.error('Failed to save lesson(s)');
    } finally {
      setLoading(false);
    }
  };

  // Academic Year Modal Handlers
  const handleAyModalOpen = (record = null) => {
    setEditingAy(record);
    if (record) {
      ayForm.setFieldsValue({
        academic_year: record.academic_year,
        start_date: record.start_date ? dayjs(record.start_date) : null,
        end_date: record.end_date ? dayjs(record.end_date) : null,
        status: record.status || 'Active'
      });
    } else {
      ayForm.resetFields();
    }
    setIsAyModalOpen(true);
  };

  const handleAySubmit = async (values) => {
    try {
      const payload = {
        academic_year: values.academic_year,
        start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : null,
        end_date: values.end_date ? values.end_date.format('YYYY-MM-DD') : null,
        status: values.status
      };
      
      if (editingAy) {
        await updateAcademicYear(editingAy.academic_year_id, payload);
        message.success('Academic year updated');
      } else {
        await createAcademicYear(payload);
        message.success('Academic year created');
      }
      setIsAyModalOpen(false);
      loadData();
    } catch (error) {
      message.error('Failed to save academic year');
    }
  };

  // Open modal to assign instructors to a selected subject
  const handleOpenAssignInstModal = (subject) => {
    setSelectedSubjectForInst(subject);
    const subIdStr = String(subject.subjects_id);

    const assignedIds = instructors
      .filter(i => {
        if (!i.subjects_id) return false;
        const ids = String(i.subjects_id).split(',').map(s => s.trim());
        return ids.includes(subIdStr);
      })
      .map(i => i.instructor_id);

    subjectInstForm.setFieldsValue({
      instructor_ids: assignedIds
    });
    setIsSubjectInstModalOpen(true);
  };

  const handleSubjectInstSubmit = async (values) => {
    if (!selectedSubjectForInst) return;
    setSubjectInstLoading(true);
    const targetSubId = String(selectedSubjectForInst.subjects_id);
    const selectedInstIds = (values.instructor_ids || []).map(id => String(id));

    try {
      const updatePromises = instructors.map(async (inst) => {
        const instIdStr = String(inst.instructor_id);
        const currentSubIds = inst.subjects_id ? String(inst.subjects_id).split(',').map(s => s.trim()).filter(Boolean) : [];
        const isCurrentlyAssigned = currentSubIds.includes(targetSubId);
        const shouldBeAssigned = selectedInstIds.includes(instIdStr);

        let newSubIds = [...currentSubIds];
        let needsUpdate = false;

        if (shouldBeAssigned && !isCurrentlyAssigned) {
          newSubIds.push(targetSubId);
          needsUpdate = true;
        } else if (!shouldBeAssigned && isCurrentlyAssigned) {
          newSubIds = newSubIds.filter(id => id !== targetSubId);
          needsUpdate = true;
        }

        if (needsUpdate) {
          const payload = {
            ...inst,
            subjects_id: newSubIds.length > 0 ? newSubIds.join(',') : null
          };
          return updateInstructor(inst.instructor_id, payload);
        }
      });

      await Promise.all(updatePromises);
      message.success('Instructor assignments updated for subject successfully!');
      setIsSubjectInstModalOpen(false);
      setSelectedSubjectForInst(null);
      subjectInstForm.resetFields();
      loadData();
    } catch (err) {
      message.error('Failed to update instructor assignments for subject.');
    } finally {
      setSubjectInstLoading(false);
    }
  };

  const standardColumns = [
    { title: 'Code', dataIndex: 'standard_code', key: 'standard_code', render: (val) => val || '—' },
    { title: 'Standard Name', dataIndex: 'standard_name', key: 'standard_name', render: (val) => <span style={{ fontWeight: 600 }}>{val}</span> },
    { title: 'Medium', dataIndex: 'standard_medium', key: 'standard_medium', render: (val) => <Tag color="blue">{val || 'English'}</Tag> },
    { title: 'Academic Year', dataIndex: 'academic_year', key: 'academic_year', render: (val) => val || '—' },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Tooltip title="Copy Standard, Subjects & Lessons to another Academic Year">
            <Button 
              size="small" 
              icon={<CopyOutlined style={{ color: '#1890ff' }} />} 
              onClick={() => handleOpenCopyModal(record)} 
            />
          </Tooltip>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleStandardModalOpen(record)} />
          <Popconfirm
            title="Delete this standard?"
            onConfirm={() => handleDelete(record.standard_id, 'standards')}
          >
            <Button danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const subjectColumns = [
    { title: 'Code', dataIndex: 'subjects_code', key: 'subjects_code' },
    { title: 'Name', dataIndex: 'subjects_name', key: 'subjects_name', render: (val) => <span style={{ fontWeight: 600 }}>{val}</span> },
    { title: 'Standard', dataIndex: 'standard_name', key: 'standard_name', render: (val, record) => val || record.subjects_standard || '—' },
    { title: 'Academic Year', dataIndex: 'academic_year', key: 'academic_year' },
    {
      title: 'Assigned Instructor(s)',
      key: 'assigned_instructors',
      render: (_, record) => {
        const subIdStr = String(record.subjects_id);
        const matched = instructors.filter(i => {
          if (!i.subjects_id) return false;
          const ids = String(i.subjects_id).split(',').map(s => s.trim());
          return ids.includes(subIdStr);
        });

        if (matched.length === 0) return <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>Unassigned</span>;

        return (
          <Space wrap size={[0, 4]}>
            {matched.map(inst => (
              <Tag key={inst.instructor_id} color="blue" style={{ fontWeight: 600, borderRadius: 4 }}>
                <UserOutlined style={{ marginRight: 4 }} />
                {inst.instructor_name}
              </Tag>
            ))}
          </Space>
        );
      }
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button 
            size="small" 
            type="primary" 
            ghost 
            icon={<UserAddOutlined />} 
            onClick={() => handleOpenAssignInstModal(record)}
          />
          <Button 
            size="small" 
            icon={<EditOutlined />} 
            onClick={() => handleSubjectModalOpen(record)}
          />
          <Popconfirm
            title="Delete this subject?"
            onConfirm={() => handleDelete(record.subjects_id, 'subjects')}
          >
            <Button danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const lessonColumns = [
    { title: 'Lesson Name', dataIndex: 'lesson_name', key: 'lesson_name' },
    { title: 'Subject', dataIndex: 'subjects_name', key: 'subjects_name' },
    { title: 'Standard', dataIndex: 'standard_name', key: 'standard_name' },
    { title: 'Sessions', dataIndex: 'lesson_total_sessions', key: 'lesson_total_sessions' },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button 
            size="small" 
            icon={<EditOutlined />} 
            onClick={() => handleLessonModalOpen(record)}
          />
          <Popconfirm
            title="Delete this lesson?"
            onConfirm={() => handleDelete(record.lessons_id, 'lessons')}
          >
            <Button danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const ayColumns = [
    { title: 'ID', dataIndex: 'academic_year_id', key: 'academic_year_id' },
    { title: 'Academic Year', dataIndex: 'academic_year', key: 'academic_year' },
    { title: 'Start Date', dataIndex: 'start_date', key: 'start_date', render: t => t ? dayjs(t).format('DD-MM-YYYY') : 'N/A' },
    { title: 'End Date', dataIndex: 'end_date', key: 'end_date', render: t => t ? dayjs(t).format('DD-MM-YYYY') : 'N/A' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: st => <Tag color={st === 'Active' ? 'green' : 'red'}>{st || 'Active'}</Tag> },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleAyModalOpen(record)} />
          <Popconfirm
            title="Delete this academic year?"
            onConfirm={() => handleDelete(record.academic_year_id, 'academic_year')}
          >
            <Button danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ color: 'var(--text-color)', margin: 0, fontWeight: 700, fontSize: 22, display: 'flex', alignItems: 'center', gap: 10 }}>
            <BookOutlined style={{ color: 'var(--primary-color)' }} />
            Academic Syllabus Management
          </h2>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Manage Standards, Subjects, Lessons, Academic Years, and Track Syllabus Progress
          </span>
        </div>
      </div>

      <Card style={{ background: 'transparent', border: '1px solid var(--border-color)', borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} type="card">
          
          {/* TAB 1: Standards */}
          <TabPane tab={<span><BranchesOutlined /> Standards</span>} key="1">
            <Card 
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-color)', borderRadius: 12, marginBottom: 20 }} 
              bodyStyle={{ padding: 16 }}
            >
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} sm={12} md={8}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Filter by Academic Year</span>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="All Academic Years"
                    allowClear
                    value={filterAyForStandards}
                    onChange={(val) => setFilterAyForStandards(val)}
                  >
                    {academicYears.map(ay => (
                      <Option key={ay.academic_year_id} value={ay.academic_year_id}>
                        {ay.academic_year} {ay.status === 'Active' || ay.status === 'ACTIVE' ? ' (Active)' : ''}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={16} style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', gap: 8 }}>
                  {filterAyForStandards !== (activeAcademicYears[0]?.academic_year_id || null) && (
                    <Button icon={<ClearOutlined />} onClick={() => setFilterAyForStandards(activeAcademicYears[0]?.academic_year_id || null)}>Reset Filter</Button>
                  )}
                  <Button 
                    icon={<CopyOutlined style={{ color: '#1890ff' }} />} 
                    onClick={() => handleOpenCopyModal(null)}
                  >
                    Copy Standard to Academic Year
                  </Button>
                  <Upload beforeUpload={(file) => handleCsvUpload(file, 'standards')} showUploadList={false} accept=".csv">
                    <Button icon={<UploadOutlined />}>Upload CSV</Button>
                  </Upload>
                  <Button icon={<DownloadOutlined />} onClick={() => downloadCSV('standards')}>Download CSV</Button>
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => handleStandardModalOpen()}>
                    Add Standard
                  </Button>
                </Col>
              </Row>
            </Card>

            <Table
              className="excel-table"
              dataSource={filteredStandards}
              columns={standardColumns}
              rowKey="standard_id"
              loading={stdLoading}
              scroll={{ x: 'max-content' }}
              pagination={{ pageSize: 10 }}
            />
          </TabPane>

          {/* TAB 2: Subjects */}
          <TabPane tab={<span><BookOutlined /> Subjects</span>} key="2">
            <Card 
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-color)', borderRadius: 12, marginBottom: 20 }} 
              bodyStyle={{ padding: 16 }}
            >
              <Row gutter={[16, 16]} align="middle">
                {/* Step 1: Academic Year Filter */}
                <Col xs={24} sm={12} md={7}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>1. Select Academic Year</span>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="All Academic Years"
                    allowClear
                    value={filterAyForSubjects}
                    onChange={(val) => {
                      setFilterAyForSubjects(val);
                      if (val && filterStdForSubjects) {
                        const match = standards.find(s => String(s.standard_id) === String(filterStdForSubjects));
                        if (match && match.academic_year_id && String(match.academic_year_id) !== String(val)) {
                          setFilterStdForSubjects(null);
                        }
                      }
                    }}
                  >
                    {academicYears.map(ay => (
                      <Option key={ay.academic_year_id} value={ay.academic_year_id}>
                        {ay.academic_year} {ay.status === 'Active' || ay.status === 'ACTIVE' ? ' (Active)' : ''}
                      </Option>
                    ))}
                  </Select>
                </Col>

                {/* Step 2: Standard Filter */}
                <Col xs={24} sm={12} md={7}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>2. Select Standard / Grade</span>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Select Standard"
                    allowClear
                    value={filterStdForSubjects}
                    onChange={(val) => setFilterStdForSubjects(val)}
                  >
                    {standardsForSubjectFilter.map(s => (
                      <Option key={s.standard_id} value={s.standard_id}>
                        {s.standard_name} ({s.standard_medium || 'English'})
                      </Option>
                    ))}
                  </Select>
                </Col>

                {/* Actions Column */}
                <Col xs={24} md={10} style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', gap: 8 }}>
                  {(filterAyForSubjects !== (activeAcademicYears[0]?.academic_year_id || null) || filterStdForSubjects) && (
                    <Button icon={<ClearOutlined />} onClick={() => { setFilterAyForSubjects(activeAcademicYears[0]?.academic_year_id || null); setFilterStdForSubjects(null); }}>Reset</Button>
                  )}
                  <Upload beforeUpload={(file) => handleCsvUpload(file, 'subjects')} showUploadList={false} accept=".csv">
                    <Button icon={<UploadOutlined />}>Upload CSV</Button>
                  </Upload>
                  <Button icon={<DownloadOutlined />} onClick={() => downloadCSV('subjects')}>Export CSV</Button>
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => handleSubjectModalOpen()}>
                    Add Subject(s)
                  </Button>
                </Col>
              </Row>
            </Card>

            <Table
              className="excel-table"
              dataSource={filteredSubjects}
              columns={subjectColumns}
              rowKey="subjects_id"
              loading={loading}
              scroll={{ x: 'max-content' }}
              pagination={{ pageSize: 10 }}
            />
          </TabPane>

          {/* TAB 3: Lessons */}
          <TabPane tab={<span><ReadOutlined /> Lessons</span>} key="3">
            <Card 
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-color)', borderRadius: 12, marginBottom: 20 }} 
              bodyStyle={{ padding: 16 }}
            >
              <Row gutter={[16, 16]} align="middle">
                {/* Step 1: Academic Year Filter */}
                <Col xs={24} sm={8} md={5}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>1. Academic Year</span>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="All Academic Years"
                    allowClear
                    value={filterAyForLessons}
                    onChange={(val) => {
                      setFilterAyForLessons(val);
                      setFilterStdForLessons(null);
                      setFilterSubForLessons(null);
                    }}
                  >
                    {academicYears.map(ay => (
                      <Option key={ay.academic_year_id} value={ay.academic_year_id}>
                        {ay.academic_year} {ay.status === 'Active' || ay.status === 'ACTIVE' ? ' (Active)' : ''}
                      </Option>
                    ))}
                  </Select>
                </Col>

                {/* Step 2: Standard Filter */}
                <Col xs={24} sm={8} md={5}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>2. Standard / Grade</span>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Select Standard"
                    allowClear
                    value={filterStdForLessons}
                    onChange={(val) => {
                      setFilterStdForLessons(val);
                      setFilterSubForLessons(null);
                    }}
                  >
                    {standardsForLessonFilter.map(s => (
                      <Option key={s.standard_id} value={s.standard_id}>
                        {s.standard_name} ({s.standard_medium || 'English'})
                      </Option>
                    ))}
                  </Select>
                </Col>

                {/* Step 3: Subject Filter */}
                <Col xs={24} sm={8} md={5}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>3. Subject Catalog</span>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Select Subject"
                    allowClear
                    value={filterSubForLessons}
                    onChange={(val) => setFilterSubForLessons(val)}
                  >
                    {subjectsForLessonFilter.map(sub => (
                      <Option key={sub.subjects_id} value={sub.subjects_id}>
                        {sub.subjects_name} ({sub.subjects_medium || 'English'})
                      </Option>
                    ))}
                  </Select>
                </Col>

                {/* Actions Column */}
                <Col xs={24} md={9} style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', gap: 8 }}>
                  {(filterAyForLessons !== (activeAcademicYears[0]?.academic_year_id || null) || filterStdForLessons || filterSubForLessons) && (
                    <Button icon={<ClearOutlined />} onClick={() => { setFilterAyForLessons(activeAcademicYears[0]?.academic_year_id || null); setFilterStdForLessons(null); setFilterSubForLessons(null); }}>Reset</Button>
                  )}
                  <Upload beforeUpload={(file) => handleCsvUpload(file, 'lessons')} showUploadList={false} accept=".csv">
                    <Button icon={<UploadOutlined />}>Upload CSV</Button>
                  </Upload>
                  <Button icon={<DownloadOutlined />} onClick={() => downloadCSV('lessons')}>Export CSV</Button>
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => handleLessonModalOpen()}>
                    Add Lesson(s)
                  </Button>
                </Col>
              </Row>
            </Card>

            <Table
              className="excel-table"
              dataSource={filteredLessons}
              columns={lessonColumns}
              rowKey="lessons_id"
              loading={loading}
              scroll={{ x: 'max-content' }}
              pagination={{ pageSize: 10 }}
            />
          </TabPane>

          {/* TAB 4: Academic Years */}
          <TabPane tab={<span><CalendarOutlined /> Academic Years</span>} key="4">
            <Card 
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-color)', borderRadius: 12, marginBottom: 20 }} 
              bodyStyle={{ padding: 16 }}
            >
              <Row gutter={[16, 16]} align="middle" justify="space-between">
                <Col xs={24} sm={12}>
                  <h3 style={{ color: 'var(--text-color)', margin: 0, fontSize: 16, fontWeight: 600 }}>
                    <CalendarOutlined style={{ marginRight: 8, color: 'var(--primary-color)' }} />
                    Academic Years Master List
                  </h3>
                </Col>
                <Col xs={24} sm={12} style={{ textAlign: 'right' }}>
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => handleAyModalOpen()}>
                    Add Academic Year
                  </Button>
                </Col>
              </Row>
            </Card>

            <Table
              className="excel-table"
              dataSource={academicYears}
              columns={ayColumns}
              rowKey="academic_year_id"
              loading={ayLoading}
              scroll={{ x: 'max-content' }}
              pagination={{ pageSize: 10 }}
            />
          </TabPane>

          {/* TAB 5: Syllabus Progress Tracker (Academic Year-wise & Cluster-wise) */}
          <TabPane tab={<span><BarChartOutlined /> Syllabus Progress</span>} key="5">
            <Card 
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-color)', borderRadius: 12, marginBottom: 20 }} 
              bodyStyle={{ padding: 16 }}
            >
              <Row gutter={[16, 16]} align="middle">
                {/* Step 1: Academic Year Filter */}
                <Col xs={24} sm={12} md={6}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>1. Select Academic Year</span>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="All Academic Years"
                    allowClear
                    value={filterAyForProgress}
                    onChange={(val) => {
                      setFilterAyForProgress(val);
                      setFilterClusterForProgress(null);
                      setFilterStdForProgress(null);
                      setFilterSubForProgress(null);
                    }}
                  >
                    {academicYears.map(ay => (
                      <Option key={ay.academic_year_id} value={ay.academic_year_id}>
                        {ay.academic_year} {ay.status === 'Active' || ay.status === 'ACTIVE' ? ' (Active)' : ''}
                      </Option>
                    ))}
                  </Select>
                </Col>

                {/* Step 2: Cluster Filter */}
                <Col xs={24} sm={12} md={6}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>2. Select Cluster</span>
                  <Select
                    style={{ width: '100%' }}
                    placeholder={isStudio ? "Select Cluster" : "All Clusters"}
                    allowClear={!isStudio}
                    value={filterClusterForProgress}
                    onChange={(val) => {
                      setFilterClusterForProgress(val);
                      setFilterStdForProgress(null);
                      setFilterSubForProgress(null);
                    }}
                  >
                    {availableClustersForProgress.map(c => (
                      <Option key={c.cluster_id} value={c.cluster_id}>
                        {c.cluster_name || `Cluster #${c.cluster_id}`} {c.cluster_code ? `(${c.cluster_code})` : ''}
                      </Option>
                    ))}
                  </Select>
                </Col>

                {/* Step 3: Standard Filter */}
                <Col xs={24} sm={12} md={6}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>3. Select Standard / Grade</span>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Select Standard"
                    allowClear
                    value={filterStdForProgress}
                    onChange={(val) => {
                      setFilterStdForProgress(val);
                      setFilterSubForProgress(null);
                    }}
                  >
                    {standardsForProgressFilter.map(s => (
                      <Option key={s.standard_id} value={s.standard_id}>
                        {s.standard_name} ({s.standard_medium || 'English'})
                      </Option>
                    ))}
                  </Select>
                </Col>

                {/* Step 4: Subject Filter */}
                <Col xs={24} sm={12} md={6}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>4. Select Subject (Optional)</span>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="All Subjects"
                    allowClear
                    value={filterSubForProgress}
                    onChange={(val) => setFilterSubForProgress(val)}
                  >
                    {subjectsForProgressFilter.map(sub => (
                      <Option key={sub.subjects_id} value={sub.subjects_id}>
                        {sub.subjects_name} ({sub.subjects_medium || 'English'})
                      </Option>
                    ))}
                  </Select>
                </Col>
              </Row>

              {(filterAyForProgress !== (activeAcademicYears[0]?.academic_year_id || null) || filterClusterForProgress || filterStdForProgress || filterSubForProgress) && (
                <div style={{ marginTop: 12, textAlign: 'right' }}>
                  <Button icon={<ClearOutlined />} onClick={() => {
                    setFilterAyForProgress(activeAcademicYears[0]?.academic_year_id || null);
                    setFilterClusterForProgress(isStudio && availableClustersForProgress.length > 0 ? availableClustersForProgress[0].cluster_id : null);
                    setFilterStdForProgress(null);
                    setFilterSubForProgress(null);
                  }}>Reset Progress Filters</Button>
                </div>
              )}
            </Card>

            {dsLoading ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Spin size="large" tip="Loading Syllabus Progress Data..." />
              </div>
            ) : clusterProgressGroups.length === 0 ? (
              <Card style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-color)', borderRadius: 12 }}>
                <Empty description="No syllabus progress data found for the selected filters." image={Empty.PRESENTED_IMAGE_SIMPLE} />
              </Card>
            ) : (
              clusterProgressGroups.map((clusterGroup) => (
                <Card
                  key={clusterGroup.clusterId}
                  style={{
                    background: 'rgba(255, 255, 255, 0.02)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 14,
                    marginBottom: 24,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)'
                  }}
                  bodyStyle={{ padding: 18 }}
                >
                  {/* Cluster Header Banner */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ width: 4, height: 20, borderRadius: 2, background: 'var(--primary-color)' }} />
                      <h4 style={{ color: 'var(--text-color)', margin: 0, fontSize: 16, fontWeight: 700 }}>
                        📍 {clusterGroup.clusterName} {clusterGroup.clusterCode ? `(${clusterGroup.clusterCode})` : ''}
                      </h4>
                      <Tag color="magenta" style={{ borderRadius: 10, fontSize: 11, padding: '0 8px', border: '1px solid rgba(235, 47, 150, 0.3)' }}>
                        {clusterGroup.academicYearName}
                      </Tag>
                      <Tag color="blue" style={{ borderRadius: 10, fontSize: 11, padding: '0 8px', border: '1px solid rgba(22, 119, 255, 0.3)' }}>
                        {clusterGroup.totalSubjects} Subjects
                      </Tag>
                      <Tag color="purple" style={{ borderRadius: 10, fontSize: 11, padding: '0 8px', border: '1px solid rgba(114, 46, 209, 0.3)' }}>
                        {clusterGroup.totalLessons} Lessons
                      </Tag>
                      <Tag color="cyan" style={{ borderRadius: 10, fontSize: 11, padding: '0 8px', border: '1px solid rgba(19, 194, 194, 0.3)' }}>
                        {clusterGroup.totalUsedSessions} / {clusterGroup.totalAllocatedSessions} Sessions Conducted
                      </Tag>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Cluster Progress: </span>
                        <strong style={{ fontSize: 14, color: clusterGroup.overallPercent === 100 ? '#52c41a' : 'var(--primary-color)' }}>
                          {clusterGroup.overallPercent}%
                        </strong>
                      </div>
                      <Progress
                        type="circle"
                        percent={clusterGroup.overallPercent}
                        width={38}
                        strokeWidth={10}
                        format={(pct) => `${pct}%`}
                        status={clusterGroup.overallPercent === 100 ? 'success' : 'normal'}
                      />
                    </div>
                  </div>

                  {/* Subjects Table for this Cluster */}
                  <Table
                    className="excel-table"
                    dataSource={clusterGroup.subjects}
                    scroll={{ x: 'max-content' }}
                    columns={[
                      {
                        title: 'Standard',
                        dataIndex: 'standard_name',
                        key: 'standard_name',
                        width: 140,
                        render: (val, record) => (
                          <Tag color="geekblue" style={{ fontWeight: 700, padding: '2px 8px', borderRadius: 4 }}>
                            {val} ({record.standard_medium || 'English'})
                          </Tag>
                        )
                      },
                      {
                        title: 'Subject Name',
                        dataIndex: 'subjects_name',
                        key: 'subjects_name',
                        render: (val, record) => (
                          <span>
                            <strong style={{ fontSize: 13, color: 'var(--text-color)' }}>{val}</strong>
                            {record.subjects_code ? <Tag color="default" style={{ marginLeft: 6, fontSize: 11 }}>{record.subjects_code}</Tag> : ''}
                          </span>
                        )
                      },
                      {
                        title: 'Lessons Count',
                        key: 'lesson_count',
                        align: 'center',
                        width: 130,
                        render: (_, record) => (
                          <Tag color="blue" style={{ fontWeight: 600 }}>{record.progress.lessonCount} Lessons</Tag>
                        )
                      },
                      {
                        title: 'Allocated Sessions',
                        key: 'allocated_sessions',
                        align: 'center',
                        width: 150,
                        render: (_, record) => (
                          <Tag color="purple" style={{ fontWeight: 700 }}>{record.progress.totalAllocated} Sessions</Tag>
                        )
                      },
                      {
                        title: 'Used Sessions',
                        key: 'used_sessions',
                        align: 'center',
                        width: 140,
                        render: (_, record) => (
                          <Tag color="cyan" style={{ fontWeight: 700 }}>{record.progress.totalUsed} Sessions</Tag>
                        )
                      },
                      {
                        title: 'Remaining',
                        key: 'remaining_sessions',
                        align: 'center',
                        width: 130,
                        render: (_, record) => (
                          <Tag color={record.progress.remaining === 0 ? 'green' : 'orange'} style={{ fontWeight: 600 }}>
                            {record.progress.remaining} Pending
                          </Tag>
                        )
                      },
                      {
                        title: 'Progress Status',
                        key: 'progress_status',
                        width: 220,
                        render: (_, record) => (
                          <Progress
                            percent={record.progress.percent}
                            size="small"
                            status={record.progress.percent === 100 ? 'success' : 'active'}
                          />
                        )
                      }
                    ]}
                    rowKey="subjects_id"
                    pagination={false}
                    summary={() => (
                      <Table.Summary.Row style={{ background: 'rgba(255, 255, 255, 0.04)', fontWeight: 'bold' }}>
                        <Table.Summary.Cell index={0} colSpan={3}>
                          <span style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            CLUSTER TOTALS ({clusterGroup.clusterName})
                          </span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="center">
                          <Tag color="blue" style={{ fontWeight: 800 }}>{clusterGroup.totalLessons} Lessons</Tag>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="center">
                          <Tag color="purple" style={{ fontWeight: 800 }}>{clusterGroup.totalAllocatedSessions} Allocated</Tag>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5} align="center">
                          <Tag color="cyan" style={{ fontWeight: 800 }}>{clusterGroup.totalUsedSessions} Conducted</Tag>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={6} align="center">
                          <Tag color={clusterGroup.totalRemainingSessions === 0 ? 'green' : 'orange'} style={{ fontWeight: 800 }}>
                            {clusterGroup.totalRemainingSessions} Pending
                          </Tag>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={7}>
                          <Progress
                            percent={clusterGroup.overallPercent}
                            size="small"
                            status={clusterGroup.overallPercent === 100 ? 'success' : 'active'}
                          />
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                    expandable={{
                      expandedRowRender: (subjectRecord) => {
                        const subLessons = sortLessonsAscending(lessons.filter(l => String(l.subjects_id) === String(subjectRecord.subjects_id)));
                        if (subLessons.length === 0) {
                          return (
                            <div style={{ padding: '12px 24px', color: 'var(--text-secondary)', fontSize: 13 }}>
                              No lessons configured for this subject yet.
                            </div>
                          );
                        }
                        return (
                          <Card size="small" style={{ margin: '8px 16px', background: 'rgba(0,0,0,0.15)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                              <h5 style={{ margin: 0, color: 'var(--primary-color)', fontSize: 13 }}>
                                📖 Lesson & Session Breakdown: {subjectRecord.subjects_name} ({clusterGroup.clusterName})
                              </h5>
                              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                                {subLessons.length} Total Lessons
                              </span>
                            </div>
                            <Table
                              size="small"
                              dataSource={subLessons}
                              rowKey="lessons_id"
                              pagination={false}
                              scroll={{ x: 'max-content' }}
                              columns={[
                                { title: '#', key: 'idx', render: (_, __, i) => i + 1, width: 50 },
                                {
                                  title: 'Lesson / Chapter Name',
                                  dataIndex: 'lesson_name',
                                  key: 'lesson_name',
                                  render: text => <strong style={{ color: 'var(--text-color)' }}>{text}</strong>
                                },
                                {
                                  title: 'Allocated Sessions',
                                  dataIndex: 'lesson_total_sessions',
                                  key: 'lesson_total_sessions',
                                  align: 'center',
                                  width: 150,
                                  render: count => <Tag color="purple">{count || 1} Sessions</Tag>
                                },
                                {
                                  title: 'Used Sessions',
                                  key: 'used_sessions',
                                  align: 'center',
                                  width: 150,
                                  render: (_, lessonRecord) => {
                                    const { usedSessions } = getLessonProgressForCluster(lessonRecord.lessons_id, clusterGroup.clusterId);
                                    return <Tag color="cyan">{usedSessions} Sessions</Tag>;
                                  }
                                },
                                {
                                  title: 'Status',
                                  key: 'status',
                                  align: 'center',
                                  width: 140,
                                  render: (_, lessonRecord) => {
                                    const { status } = getLessonProgressForCluster(lessonRecord.lessons_id, clusterGroup.clusterId);
                                    if (!status) return <Tag color="default">PENDING</Tag>;
                                    const upperStatus = status.toUpperCase();
                                    if (upperStatus === 'COMPLETED') {
                                      return <Tag color="success">COMPLETED</Tag>;
                                    } else if (upperStatus === 'PENDING') {
                                      return <Tag color="warning">PENDING</Tag>;
                                    } else if (upperStatus === 'IN_PROGRESS' || upperStatus === 'ONGOING') {
                                      return <Tag color="processing">IN PROGRESS</Tag>;
                                    } else {
                                      return <Tag color="blue">{upperStatus}</Tag>;
                                    }
                                  }
                                },
                                {
                                  title: 'Lesson Progress',
                                  key: 'lesson_progress',
                                  width: 160,
                                  render: (_, lessonRecord) => {
                                    const { usedSessions } = getLessonProgressForCluster(lessonRecord.lessons_id, clusterGroup.clusterId);
                                    const alloc = Number(lessonRecord.lesson_total_sessions) || 1;
                                    const pct = Math.min(100, Math.round((usedSessions / alloc) * 100));
                                    return <Progress percent={pct} size="small" status={pct >= 100 ? 'success' : 'active'} />;
                                  }
                                }
                              ]}
                            />
                          </Card>
                        );
                      }
                    }}
                  />
                </Card>
              ))
            )}
          </TabPane>

        </Tabs>
      </Card>

      {/* Standard Modal */}
      <Modal
        title={editingStandard ? 'Edit Standard' : 'Add Standard'}
        open={isStandardModalOpen}
        onCancel={() => setIsStandardModalOpen(false)}
        onOk={() => standardForm.submit()}
        destroyOnClose
      >
        <Form form={standardForm} layout="vertical" onFinish={handleStandardSubmit}>
          <Form.Item name="academic_year_id" label="Academic Year" rules={[{ required: true, message: 'Please select academic year' }]}>
            <Select placeholder="Select Active Academic Year">
              {activeAcademicYears.map(ay => (
                <Option key={ay.academic_year_id} value={ay.academic_year_id}>{ay.academic_year}</Option>
              ))}
            </Select>
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="standard_name" label="Standard Name" rules={[{ required: true, message: 'Please enter standard name' }]}>
                <Input placeholder="e.g. Standard 10th" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="standard_code" label="Standard Code">
                <Input placeholder="e.g. STD10" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="standard_medium" label="Medium">
            <Select placeholder="Select Medium" allowClear>
              <Option value="English">English</Option>
              <Option value="Kannada">Kannada</Option>
              <Option value="Gujarati">Gujarati</Option>
              <Option value="Hindi">Hindi</Option>
              <Option value="Marathi">Marathi</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Copy Standard & Syllabus to Academic Year Modal */}
      <Modal
        title={
          <Space>
            <CopyOutlined style={{ color: '#1890ff', fontSize: 18 }} />
            <span style={{ fontWeight: 600 }}>
              {copyModalStandard ? `Copy Standard: ${copyModalStandard.standard_name}` : 'Copy Standard(s) to Academic Year'}
            </span>
          </Space>
        }
        open={isCopyModalOpen}
        onCancel={() => { setIsCopyModalOpen(false); setCopyModalStandard(null); copyForm.resetFields(); }}
        onOk={() => copyForm.submit()}
        confirmLoading={copyLoading}
        okText="Copy Syllabus"
        width={620}
        destroyOnClose
      >
        <Form 
          form={copyForm} 
          layout="vertical" 
          onFinish={handleCopySubmit}
          initialValues={{ copy_subjects: true, copy_lessons: true }}
        >
          <div style={{ marginBottom: 16, padding: '12px 16px', background: 'rgba(24, 144, 255, 0.08)', borderRadius: 8, border: '1px solid rgba(24, 144, 255, 0.2)' }}>
            <span style={{ fontSize: 13, color: '#1890ff', lineHeight: 1.5, display: 'block' }}>
              Clone standard(s), their associated subjects, and lesson topics into the selected target academic year.
            </span>
          </div>

          <Form.Item 
            name="source_standard_ids" 
            label="Source Standard(s) to Copy" 
            rules={[{ required: true, message: 'Please select at least one standard to copy' }]}
          >
            <Select 
              mode="multiple" 
              placeholder="Select Standard(s)"
              style={{ width: '100%' }}
              optionFilterProp="children"
            >
              {standards.map(s => {
                const ay = academicYears.find(a => a.academic_year_id === s.academic_year_id);
                return (
                  <Option key={s.standard_id} value={s.standard_id}>
                    {s.standard_name} ({s.standard_medium || 'English'}) {ay ? `— [${ay.academic_year}]` : ''}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item 
            name="target_academic_year_id" 
            label="Target Academic Year (Copy To)" 
            rules={[{ required: true, message: 'Please select target academic year' }]}
          >
            <Select placeholder="Select Target Academic Year">
              {academicYears.map(ay => (
                <Option key={ay.academic_year_id} value={ay.academic_year_id}>
                  {ay.academic_year} {ay.status === 'Active' || ay.status === 'ACTIVE' ? ' (Active)' : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Card size="small" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-color)', borderRadius: 8, marginTop: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 13 }}>Include Components in Copy:</div>
            
            <Form.Item name="copy_subjects" valuePropName="checked" style={{ marginBottom: 10 }}>
              <Checkbox defaultChecked>
                <span style={{ fontSize: 13 }}>
                  <strong>Include All Subjects</strong> (Clones subject names, codes, medium, and links to the cloned standard)
                </span>
              </Checkbox>
            </Form.Item>

            <Form.Item name="copy_lessons" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox defaultChecked>
                <span style={{ fontSize: 13 }}>
                  <strong>Include All Lessons / Topics</strong> (Clones lesson names, medium, total sessions, and links to cloned subjects)
                </span>
              </Checkbox>
            </Form.Item>
          </Card>
        </Form>
      </Modal>

      {/* Subject Modal (Cascading: Academic Year -> Standard -> Subjects) */}
      <Modal
        title={editingSubject ? 'Edit Subject' : 'Add Subject(s)'}
        open={isSubjectModalOpen}
        onCancel={() => { setIsSubjectModalOpen(false); setEditingSubject(null); subjectForm.resetFields(); }}
        onOk={() => subjectForm.submit()}
        confirmLoading={loading}
        width={680}
        destroyOnClose
      >
        <Form form={subjectForm} layout="vertical" onFinish={handleSubjectSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="academic_year_id" label="1. Select Academic Year" rules={[{ required: true, message: 'Please select academic year' }]}>
                <Select 
                  placeholder="Select Active Academic Year"
                  onChange={(val) => {
                    setSubjectFormAyId(val);
                    const validStds = standards.filter(s => !s.academic_year_id || String(s.academic_year_id) === String(val));
                    subjectForm.setFieldsValue({ standard_id: validStds[0]?.standard_id || null });
                  }}
                >
                  {activeAcademicYears.map(ay => (
                    <Option key={ay.academic_year_id} value={ay.academic_year_id}>{ay.academic_year}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="standard_id" label="2. Select Standard" rules={[{ required: true, message: 'Please select standard' }]}>
                <Select placeholder="Select Standard">
                  {availableStandardsForSubjectModal.map(s => (
                    <Option key={s.standard_id} value={s.standard_id}>{s.standard_name} ({s.standard_medium || 'English'})</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: '12px 0 16px' }} />

          {editingSubject ? (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="subjects_name" label="Subject Name" rules={[{ required: true, message: 'Please enter subject name' }]}>
                    <Input placeholder="e.g. Science & Technology" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="subjects_code" label="Subject Code">
                    <Input placeholder="e.g. SCI-101" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="subjects_medium" label="Medium">
                <Select placeholder="Select Medium">
                  <Option value="English">English</Option>
                  <Option value="Kannada">Kannada</Option>
                  <Option value="Gujarati">Gujarati</Option>
                  <Option value="Hindi">Hindi</Option>
                </Select>
              </Form.Item>
            </>
          ) : (
            <Form.List name="subjects">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <Card 
                      key={key} 
                      size="small" 
                      style={{ marginBottom: 12, background: 'rgba(255,255,255,0.02)', borderColor: 'var(--border-color)' }}
                      title={<span style={{ fontSize: 13, fontWeight: 600 }}>Subject #{index + 1}</span>}
                      extra={fields.length > 1 ? (
                        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)}>
                          Remove
                        </Button>
                      ) : null}
                    >
                      <Row gutter={12}>
                        <Col span={10}>
                          <Form.Item
                            {...restField}
                            name={[name, 'subjects_name']}
                            label="Subject Name"
                            rules={[{ required: true, message: 'Enter subject name' }]}
                            style={{ marginBottom: 8 }}
                          >
                            <Input placeholder="e.g. Science / Mathematics" />
                          </Form.Item>
                        </Col>
                        <Col span={7}>
                          <Form.Item
                            {...restField}
                            name={[name, 'subjects_code']}
                            label="Subject Code"
                            style={{ marginBottom: 8 }}
                          >
                            <Input placeholder="e.g. SCI-101" />
                          </Form.Item>
                        </Col>
                        <Col span={7}>
                          <Form.Item
                            {...restField}
                            name={[name, 'subjects_medium']}
                            label="Medium"
                            style={{ marginBottom: 8 }}
                          >
                            <Select placeholder="Select Medium">
                              <Option value="English">English</Option>
                              <Option value="Kannada">Kannada</Option>
                              <Option value="Gujarati">Gujarati</Option>
                              <Option value="Hindi">Hindi</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  <Button 
                    type="dashed" 
                    onClick={() => add({ subjects_name: '', subjects_code: '', subjects_medium: 'English' })} 
                    block 
                    icon={<PlusOutlined />}
                    style={{ marginTop: 8 }}
                  >
                    Add Another Subject
                  </Button>
                </>
              )}
            </Form.List>
          )}
        </Form>
      </Modal>

      {/* Lesson Modal (Cascading: Academic Year -> Standard -> Subject -> Lessons) */}
      <Modal
        title={editingLesson ? 'Edit Lesson' : 'Add Lesson(s)'}
        open={isLessonModalOpen}
        onCancel={() => { setIsLessonModalOpen(false); setEditingLesson(null); lessonForm.resetFields(); }}
        onOk={() => lessonForm.submit()}
        confirmLoading={loading}
        width={680}
        destroyOnClose
      >
        <Form form={lessonForm} layout="vertical" onFinish={handleLessonSubmit}>
          <Row gutter={12}>
            {/* 1. Academic Year */}
            <Col span={8}>
              <Form.Item name="academic_year_id" label="1. Academic Year" rules={[{ required: true, message: 'Select academic year' }]}>
                <Select 
                  placeholder="Select Active AY"
                  onChange={(val) => {
                    setLessonFormAyId(val);
                    const validStds = standards.filter(s => !s.academic_year_id || String(s.academic_year_id) === String(val));
                    const newStd = validStds[0]?.standard_id || null;
                    setLessonFormStdId(newStd);

                    const validSubs = subjects.filter(sub => {
                      const matchAy = !val || !sub.academic_year_id || String(sub.academic_year_id) === String(val);
                      const matchStd = !newStd || String(sub.standard_id) === String(newStd);
                      return matchAy && matchStd;
                    });
                    lessonForm.setFieldsValue({
                      standard_id: newStd,
                      subjects_id: validSubs[0]?.subjects_id || null
                    });
                  }}
                >
                  {activeAcademicYears.map(ay => (
                    <Option key={ay.academic_year_id} value={ay.academic_year_id}>{ay.academic_year}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* 2. Standard */}
            <Col span={8}>
              <Form.Item name="standard_id" label="2. Standard" rules={[{ required: true, message: 'Select standard' }]}>
                <Select 
                  placeholder="Select Standard"
                  onChange={(val) => {
                    setLessonFormStdId(val);
                    const validSubs = subjects.filter(sub => {
                      const matchAy = !lessonFormAyId || !sub.academic_year_id || String(sub.academic_year_id) === String(lessonFormAyId);
                      const matchStd = !val || String(sub.standard_id) === String(val);
                      return matchAy && matchStd;
                    });
                    lessonForm.setFieldsValue({ subjects_id: validSubs[0]?.subjects_id || null });
                  }}
                >
                  {availableStandardsForLessonModal.map(s => (
                    <Option key={s.standard_id} value={s.standard_id}>{s.standard_name} ({s.standard_medium || 'English'})</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* 3. Subject */}
            <Col span={8}>
              <Form.Item name="subjects_id" label="3. Subject" rules={[{ required: true, message: 'Select subject' }]}>
                <Select placeholder="Select Subject">
                  {availableSubjectsForLessonModal.map(sub => (
                    <Option key={sub.subjects_id} value={sub.subjects_id}>{sub.subjects_name} ({sub.subjects_medium || 'English'})</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: '12px 0 16px' }} />

          {editingLesson ? (
            <Row gutter={16}>
              <Col span={16}>
                <Form.Item name="lesson_name" label="Lesson Name" rules={[{ required: true, message: 'Please enter lesson name' }]}>
                  <Input placeholder="e.g. Chapter 1: Real Numbers" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="lesson_total_sessions" label="Sessions Required">
                  <InputNumber min={1} max={100} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          ) : (
            <Form.List name="lessons">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <Card 
                      key={key} 
                      size="small" 
                      style={{ marginBottom: 12, background: 'rgba(255,255,255,0.02)', borderColor: 'var(--border-color)' }}
                      title={<span style={{ fontSize: 13, fontWeight: 600 }}>Lesson #{index + 1}</span>}
                      extra={fields.length > 1 ? (
                        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)}>
                          Remove
                        </Button>
                      ) : null}
                    >
                      <Row gutter={12}>
                        <Col span={16}>
                          <Form.Item
                            {...restField}
                            name={[name, 'lesson_name']}
                            label="Lesson Name"
                            rules={[{ required: true, message: 'Enter lesson name' }]}
                            style={{ marginBottom: 8 }}
                          >
                            <Input placeholder="e.g. Chapter 1: Real Numbers" />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, 'lesson_total_sessions']}
                            label="Sessions"
                            style={{ marginBottom: 8 }}
                          >
                            <InputNumber min={1} max={100} style={{ width: '100%' }} placeholder="e.g. 3" />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  <Button 
                    type="dashed" 
                    onClick={() => add({ lesson_name: '', lesson_total_sessions: 1 })} 
                    block 
                    icon={<PlusOutlined />}
                    style={{ marginTop: 8 }}
                  >
                    Add Another Lesson
                  </Button>
                </>
              )}
            </Form.List>
          )}
        </Form>
      </Modal>

      {/* Academic Year Modal */}
      <Modal
        title={editingAy ? 'Edit Academic Year' : 'Add Academic Year'}
        open={isAyModalOpen}
        onCancel={() => setIsAyModalOpen(false)}
        onOk={() => ayForm.submit()}
        destroyOnClose
      >
        <Form form={ayForm} layout="vertical" onFinish={handleAySubmit}>
          <Form.Item name="academic_year" label="Academic Year Name" rules={[{ required: true, message: 'Please enter academic year name' }]}>
            <Input placeholder="e.g. 2024-2025" />
          </Form.Item>
          <Form.Item name="start_date" label="Start Date">
            <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
          </Form.Item>
          <Form.Item name="end_date" label="End Date">
            <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="Active">
            <Select showSearch optionFilterProp="children">
              <Option value="Active">Active</Option>
              <Option value="Inactive">Inactive</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Assign Instructor(s) to Subject Modal */}
      <Modal
        title={
          selectedSubjectForInst ? (
            <span>
              <BookOutlined style={{ color: '#1890ff', marginRight: 8 }} />
              Assign Instructor(s) for <strong>{selectedSubjectForInst.standard_name || selectedSubjectForInst.subjects_standard || ''} {selectedSubjectForInst.subjects_name}</strong>
            </span>
          ) : 'Assign Instructor(s)'
        }
        open={isSubjectInstModalOpen}
        onCancel={() => { setIsSubjectInstModalOpen(false); setSelectedSubjectForInst(null); subjectInstForm.resetFields(); }}
        onOk={() => subjectInstForm.submit()}
        confirmLoading={subjectInstLoading}
        okText="Save Assignments"
        destroyOnClose
      >
        <Form form={subjectInstForm} layout="vertical" onFinish={handleSubjectInstSubmit}>
          <Form.Item 
            name="instructor_ids" 
            label="Select Assigned Instructor(s) (Multiple instructors can teach this subject)"
          >
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="children"
              placeholder="Select instructor(s) for this subject"
              allowClear
              style={{ width: '100%' }}
            >
              {instructors.map(inst => (
                <Option key={inst.instructor_id} value={inst.instructor_id}>
                  {inst.instructor_name} {inst.instructor_qualification ? `(${inst.instructor_qualification})` : ''} {inst.instructor_medium ? `[${inst.instructor_medium}]` : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── CSV Import Preview Modal ── */}
      <Modal
        title={`Preview CSV ${csvUploadType === 'subjects' ? 'Subjects' : csvUploadType === 'lessons' ? 'Lessons' : 'Standards'} Import`}
        open={isCsvModalOpen}
        onCancel={() => { setIsCsvModalOpen(false); setCsvParsedData([]); }}
        onOk={handleConfirmCsvUpload}
        confirmLoading={uploadingCsv}
        width={1050}
        okText={`Import ${csvUploadType === 'subjects' ? 'Subjects' : csvUploadType === 'lessons' ? 'Lessons' : 'Standards'}`}
        style={{ top: 20 }}
      >
        <p>Found <strong>{csvParsedData.length}</strong> {csvUploadType} records in the uploaded file.</p>
        <Table
          dataSource={csvParsedData}
          rowKey={(rec, index) => index}
          size="small"
          bordered
          pagination={{ pageSize: 30, showSizeChanger: true, pageSizeOptions: ['30', '50', '100'] }}
          scroll={{ x: 'max-content' }}
          columns={
            csvUploadType === 'subjects'
              ? [
                  { title: 'SUBJECT NAME', dataIndex: 'subjects_name' },
                  { title: 'CODE', dataIndex: 'subjects_code', render: val => val || '—' },
                  { title: 'MEDIUM', dataIndex: 'subjects_medium', render: val => <Tag color="cyan">{val}</Tag> },
                  { title: 'STANDARD', dataIndex: 'standard_name', render: val => val || '—' },
                  { title: 'ACADEMIC YEAR', dataIndex: 'academic_year_name', render: val => val || '—' }
                ]
              : csvUploadType === 'lessons'
              ? [
                  { title: 'LESSON NAME', dataIndex: 'lesson_name' },
                  { title: 'SUBJECT', dataIndex: 'subjects_name', render: val => val || '—' },
                  { title: 'STANDARD', dataIndex: 'standard_name', render: val => val || '—' },
                  { title: 'ACADEMIC YEAR', dataIndex: 'academic_year_name', render: val => val || '—' },
                  { title: 'MEDIUM', dataIndex: 'lesson_medium', render: val => <Tag color="cyan">{val}</Tag> },
                  { title: 'TOTAL SESSIONS', dataIndex: 'lesson_total_sessions', render: val => <Tag color="purple">{val || 1}</Tag> }
                ]
              : [
                  { title: 'STANDARD NAME', dataIndex: 'standard_name' },
                  { title: 'MEDIUM', dataIndex: 'standard_medium', render: val => <Tag color="cyan">{val}</Tag> },
                  { title: 'STREAM', dataIndex: 'standard_stream', render: val => val || '—' },
                  { title: 'ACADEMIC YEAR', dataIndex: 'academic_year_name', render: val => val || '—' }
                ]
          }
        />
      </Modal>
    </div>
  );
};

export default SyllabusManagementPage;
